import React, {useCallback, useContext, useEffect, useState} from 'react';
import cx from 'classnames';
import {Icon, Title, PriorityIcon, noop} from '@priority-software/priority-style-react';
import {BankAccount} from '../BankAccount';
import {PrioriotyInfoScreen} from '../Questionnaire';
import {Summary} from '../Summary';
import {defaultTabsData, tabsTitleMap} from '../../constants';
import {CompanyDetails} from '../CompanyDetails';
import {ErrorScreen} from '../Error/ErrorScreens';
import {
    getOnboardingState,
    getTabsData,
    setOnboardingStep,
    setTabsDataToStore
} from '../../service/completionState';
import {OnboardingCtx} from '../../Onboarding.context';
import {FormWarningAlert} from '../../components/Dialogs/FormWarningAlert';
import texts from '../../texts';
import styles from './styles.module.scss';
import {sendMonitor} from '../../../../utils/monitoring';

type Props = {
    onClose: () => void;
};

const contentMap = {
    [defaultTabsData[0].id]: CompanyDetails,
    [defaultTabsData[1].id]: BankAccount,
    [defaultTabsData[2].id]: PrioriotyInfoScreen,
    [defaultTabsData[3].id]: Summary
};

export const OnboardingTabsContext = React.createContext<any>({tabs: [], goToTab: noop});
export const OnboardingTabsContextProvider = OnboardingTabsContext.Provider;

export const TabsContent = (props: Props) => {
    const {check_unsaved_data, leave_OB_process, close_OB_dialog} = useContext(OnboardingCtx);
    const [tabs, setTabs] = useState(getTabsData());

    const defaultOpenTab = tabs[getOnboardingState().OB_step];
    const [currentTab, setCurrentTab] = useState(defaultOpenTab);

    const [connectionError, toggleConnectionError] = useState(false);

    const goToTab = useCallback(
        async (stepID: string = '') => {
            if (stepID) {
                const tab = tabs[stepID];
                await setOnboardingStep(stepID);
                setCurrentTab(tab);
            } else {
                console.error(`Incorrect step ID: ${stepID}`);
            }
        },
        [setCurrentTab, tabs]
    );

    const onGoOutside = useCallback(
        navCallback => {
            const onLeave = () => {
                if (navCallback) navCallback();
                leave_OB_process();
            };
            close_OB_dialog(onLeave);
        },
        [leave_OB_process, close_OB_dialog]
    );

    const monitorTabs = (id: string) => {
        let monitorText = 'OnboardingTabsChange';
        switch (id) {
            case '0':
                monitorText += 'Company';
                break;
            case '1':
                monitorText += 'Bank';
                break;
            case '2':
                monitorText += 'Questions';
                break;
            case '3':
                monitorText += 'Summary';
                break;
            default:
                return;
        }
        sendMonitor(monitorText);
    };

    const onTabClick = useCallback(
        e => {
            e.preventDefault();
            const id = e.currentTarget.id;
            monitorTabs(id);
            const dataCheckCallback = () => goToTab(id); // create a callback func to change tab, after data check

            check_unsaved_data(dataCheckCallback); // Looking for unsaved data, and passing callback to proceed
        },
        [goToTab, check_unsaved_data]
    );

    const setComplete = useCallback(async () => {
        const nextStepIndex = Number(currentTab.id) + 1;

        if (tabs[nextStepIndex]) {
            setCurrentTab(tabs[nextStepIndex]);
        }

        const updatedData = tabs.map(tab => (tab.id === currentTab.id ? {...tab, ready: 1} : tab));

        await setTabsDataToStore(updatedData);

        setTabs(updatedData);
    }, [currentTab, setTabs, setCurrentTab, tabs]);

    const TabContent = contentMap[currentTab.id];

    useEffect(() => {
        // handle offline error screen
        const onOffline = e => toggleConnectionError(true);
        const onOnline = e => toggleConnectionError(false);

        window.addEventListener('offline', onOffline);
        window.addEventListener('online', onOnline);

        return () => {
            window.removeEventListener('offline', onOffline);
            window.removeEventListener('online', onOnline);
        };
    }, []);

    function renderContent() {
        if (connectionError) {
            return <ErrorScreen />;
        } else if (TabContent) {
            return (
                <TabContent
                    onGoOutside={onGoOutside}
                    onCloseModal={props.onClose}
                    isReadOnly={currentTab.isFinished}
                    id={currentTab.id}
                    onSubmit={setComplete}
                />
            );
        }
        return null;
    }

    return (
        <OnboardingTabsContextProvider value={{tabs, goToTab: onTabClick}}>
            <div
                className={cx(styles.component, 'onboarding-content-wrap')}
                id="onboarding-content"
            >
                <aside className={styles.sidebar}>
                    <Title textColor="primary-700" componentClasses={styles.title} skin="h4">
                        {texts.onboarding_tabs_title}
                    </Title>

                    <nav>
                        {tabs.map(item => {
                            const isActive = item.id === currentTab.id;

                            return (
                                <div
                                    key={item.id}
                                    className={cx(styles.navItem, {
                                        [styles.active]: isActive,
                                        [styles.ready]: !!item.ready && !isActive
                                    })}
                                    id={item.id}
                                    onClick={onTabClick}
                                >
                                    <Title
                                        fontWeight="normal"
                                        componentClasses={styles.navTitle}
                                        skin="h6"
                                        textColor={isActive ? 'white' : 'secondary-400'}
                                    >
                                        {tabsTitleMap[item.id]}
                                    </Title>

                                    {Boolean(item.ready) && (
                                        <Icon
                                            iconColor={isActive ? 'white' : 'primary-500'}
                                            icon={PriorityIcon.check}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </nav>
                </aside>

                <div className={styles.tabContent}>{renderContent()}</div>

                <FormWarningAlert />
            </div>
        </OnboardingTabsContextProvider>
    );
};
